import { Interval } from '@ui/constants/poll';
import { usePendingLimitOrdersStore } from '@ui/features/Websocket/usePendingLimitOrdersStore';
import { useDataChainId } from '@ui/hooks/datachain';
import { useBackendPairsAll } from '@ui/services/backend/hooks/getPairs/useBackendPairsAll';
import { useGetLimitOrdersBatch } from '@ui/services/contract/hooks/LimitOrderFacet/useGetLimitOrdersBatch/useGetLimitOrdersBatch';
import { useEffect, useMemo } from 'react';

import { useClosingOrdersStore } from './useClosingOrdersStore';

// Clean up pending limit orders when they appear in confirmed orders
export const useRemoveOrderPendingData = () => {
  const { data: pairs } = useBackendPairsAll();
  const chainId = useDataChainId();
  const pendingLimitOrders = usePendingLimitOrdersStore((s) =>
    s.usePendingLimitOrders(),
  );
  const removePendingLimitOrder = usePendingLimitOrdersStore(
    (s) => s.removePendingLimitOrder,
  );
  const closingOrders = useClosingOrdersStore((s) => s.useClosingOrders());

  const { data: limitOrdersQueryData } = useGetLimitOrdersBatch(
    useMemo(
      () => ({
        pairs: pairs,
        interval: Interval.SLOW,
      }),
      [pairs],
    ),
  );

  useEffect(() => {
    // deal with pending limit orders
    if (limitOrdersQueryData && pendingLimitOrders.length > 0) {
      const confirmedOrderIds = new Set(
        limitOrdersQueryData.map((order) => order.id),
      );

      // Check each pending limit order
      pendingLimitOrders.forEach((pendingOrder) => {
        // If the pending order is now in the confirmed orders, remove it from pending
        if (confirmedOrderIds.has(pendingOrder.id)) {
          removePendingLimitOrder(pendingOrder.id, chainId);
        }
      });
    }

    // deal with closing orders
    if (limitOrdersQueryData && closingOrders.length > 0) {
      // if can not find closing order in limitOrdersQueryData, remove it from closingOrders
      closingOrders.forEach((closingOrder) => {
        const orderExists = limitOrdersQueryData.some(
          (order) => order.id === closingOrder,
        );
        if (!orderExists) {
          // Remove the closing order from the store
          useClosingOrdersStore
            .getState()
            .removeClosingOrder(closingOrder, chainId);
        }
      });
    }
  }, [
    limitOrdersQueryData,
    pendingLimitOrders,
    removePendingLimitOrder,
    closingOrders,
    chainId,
  ]);
};
